/* eslint-disable react/prop-types */

import React, { useContext, useState, useEffect } from 'react';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  AppBar,
  Badge,
  Toolbar,
  makeStyles,
  Button,
  Menu,
  MenuItem,
  Avatar,
} from '@material-ui/core';

import axios from 'axios';
import { useHistory, useLocation } from 'react-router-dom';
import DashboardOutlinedIcon from '@material-ui/icons/DashboardOutlined';
import TouchAppOutlinedIcon from '@material-ui/icons/TouchAppOutlined';
import ChatBubbleOutlineOutlinedIcon from '@material-ui/icons/ChatBubbleOutlineOutlined';
import LinkIcon from '@material-ui/icons/Link';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { useCookies } from 'react-cookie';
import Sidebar, { drawerWidth } from '../sidebar/Sidebar';
import { storeContext } from '../provider/Provider';
import UserLabel from '../user-label/UserLabel';
import { authActions } from '../../store/auth-reducer';
import { websiteSwitchActions } from '../../store/website-switch-reducer';
import { websiteAnalyticsActions } from '../../store/website-analytics-reducer';
import { leadsActions } from '../../store/leads-reducer';
import { contentActions } from '../../store/content-reducer';
import { ppcActions } from '../../store/ppc-reducer';
import analytics from '../../images/icon-analytics.svg';
import leads from '../../images/icon-leads.svg';
import content from '../../images/icon-content.svg';
import analyticsBlue from '../../images/icon-analytics-blue.svg';
import leadsBlue from '../../images/icon-leads-blue.svg';
import contentBlue from '../../images/icon-content-blue.svg';
import opportunitiesBlue from '../../images/icon-opportunities-blue.svg';
import opportunities from '../../images/icon-opportunities.svg';

const { REACT_APP_AUTH0_URL, REACT_APP_AUTH0_CLIENT_ID } = process.env;

/*
 * StaticSidebar uses custom Sidebar component */

const notificationStyles = {
  background: '#D81F27',
  borderRadius: 3,
  color: '#fff',
  fontStyle: 'normal',
  fontWeight: 'bold',
  marginLeft: 8,
  fontSize: 14,
  padding: '0 6px',
};

const useStyles = makeStyles(() => ({
  appBar: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    background: 'white',
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'row-reverse',
  },
  imgLogo: {
    width: '1.5rem',
  },
  greyColorCustomizer: {
    color: '#C2CFE0',
  },
  blueColorCustomizer: {
    color: '#00B5E2',
  },
  listItemFocused: {
    color: '#00B5E2',
  },
  siteAvatar: {
    '& > img': {
      width: '100%',
    },
  },
  menu: {
    top: 52,
    '& .MuiMenuItem-root': {
      fontSize: '1.3rem',
    },
  },
  list: {
    '& .MuiTypography-body1': {
      fontSize: '1.3rem',
    },
  },
  themeBadge: {
    backgroundColor: '#D81F27',
    color: 'white',
  },
  listChild: {
    margin: '0 0 0 56px',
    padding: 0,
  },
  listItemChild: {
    margin: 0,
    padding: 0,
    '&:hover': {
      background: 'none',
    },
    '&:hover .MuiListItemText-secondary': {
      color: '#00B5E2',
    },
  },
  listItemChildText: {
    '& .MuiListItemText-secondary': {
      color: '#58595B',
      fontSize: '16px',
    },
  },
  listItemChildTextActive: {
    '& .MuiListItemText-secondary': {
      color: '#00B5E2',
      fontSize: '16px',
    },
  },
  notificationsCount: {
    ...notificationStyles,
  },
  notificationsCountSmall: {
    ...notificationStyles,
    position: 'absolute',
    padding: '0 5px',
    fontSize: 10,
    right: 35,
    top: 5,
  },
}));

const StaticSidebar = ({hasAccessGranted = true}) => {
  const [menuTrigger, setMenuTrigger] = useState(null);
  const [imageNotFound, setImageNotFound] = useState(false);
  const [avatarCopy, setAvatarCopy] = useState('HD');
  const [news, setNews] = useState(0);
  const [, , removeCookie] = useCookies(['websiteSwitch']);
  const location = useLocation();
  const currentPage = location.pathname.replace('/', '');
  const { state, dispatch } = useContext(storeContext);
  const history = useHistory();
  const classes = useStyles();
  const [leadsExist, setLeadsExist] = useState(false)

  useEffect(() => {
    if (state.websiteSwitch.currentWebsite === null)
      return;

    setAvatarCopy(state.websiteSwitch.currentWebsite.substring(0, 2).toUpperCase());
  }, [state.websiteSwitch.currentWebsite]);

  useEffect(() => {
    if (state.campaignUpdates?.newMessagesCount === null)
      return;

    setNews(state.campaignUpdates?.newMessagesCount);
  }, [state.campaignUpdates]);

  const onError = () => setImageNotFound(true);

  const onLoad = () => setImageNotFound(false);

  const handleMenuOpen = (event) => {
    setMenuTrigger(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuTrigger(null);
  };

  const handleLogout = () => {
    dispatch({ type: authActions.LOGOUT });
    dispatch({ type: websiteSwitchActions.CLEAN_WEBSITE_SWITCH });
    dispatch({ type: websiteAnalyticsActions.WEBSITE_ANALYTICS_CLEAN });
    dispatch({ type: leadsActions.LEADS_CLEAN });
    dispatch({ type: contentActions.CONTENT_CLEAN });
    dispatch({ type: ppcActions.PPC_CLEAN });
    window.location.href = `${REACT_APP_AUTH0_URL}/logout?client_id=${REACT_APP_AUTH0_CLIENT_ID}&returnTo=${window.location.origin}`;
  };

  const switchWebsite = () => {
    dispatch({ type: websiteSwitchActions.CLEAN_WEBSITE_CHOSEN });
    dispatch({ type: websiteAnalyticsActions.WEBSITE_ANALYTICS_CLEAN });
    dispatch({ type: leadsActions.LEADS_CLEAN });
    dispatch({ type: contentActions.CONTENT_CLEAN });
    removeCookie('websiteSwitch');
    history.push('/switch-website');
  };
  const openSettings = () => {
    history.push('/settings');
  };

  const openCampaignUpdates = () => {
    history.push('/campaign-updates');
  };

  useEffect(() => {
    const fetchLeadsCount = async () => {
      if (!state.websiteSwitch.currentWebsite) {
        return;
      }
      try {
        const res = await axios.get(`${process.env.REACT_APP_FLASK_API_URL}/v1/leads/${state.websiteSwitch.currentWebsite}/1/10?marked_as_lead=true`);
        if (res?.data?.data?.total > 0) {
          setLeadsExist(true);
        }
      } catch (error) {
          // 
      }
    }

    if (state.websiteSwitch.currentWebsite) {
      fetchLeadsCount();
    }

  }, [state.websiteSwitch.currentWebsite, state?.leads?.leads])

  return (
    <>
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar>
          {state.websiteSwitch.currentWebsiteSettings.assignPageFeature &&
          state.content.assignPageFeature.total ? (
            <Button
              aria-controls="simple-menu"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              aria-label="open menu"
            >
              {state.websiteSwitch.currentWebsite ? (
                <>
                  <span style={{ fontSize: 18, marginRight: 15, color: '#58595B' }}>
                    {state.websiteSwitch.currentWebsite}
                  </span>
                  <Badge
                    badgeContent={state.content.assignPageFeature.total}
                    classes={{ badge: classes.themeBadge }}
                  >
                    {imageNotFound === false ? <Avatar
                      alt="Site Icon"
                      className={classes.siteAvatar}
                    >
                      <img
                        src={`https://hd-platform-icons.s3.us-west-1.amazonaws.com/Profile+Images/${state.websiteSwitch.currentWebsite}.png`}
                        onError={onError}
                        onLoad={onLoad}
                        alt="Site Icon"
                      />
                    </Avatar> : 
                    <Avatar>{avatarCopy}</Avatar>
                    }
                    {news && state.campaignUpdates?.newMessagesCount ? (
                      <span className={classes.notificationsCountSmall}>
                        {news}
                      </span>
                    ) : (
                      ''
                    )}
                  </Badge>
                </>
              ) : (
                <Avatar>HD</Avatar>
              )}
            </Button>
          ) : (
            <Button
              aria-controls="simple-menu"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              aria-label="open menu"
            >
              {state.websiteSwitch.currentWebsite ? (
                <>
                  <span style={{ fontSize: 18, marginRight: 15, color: '#58595B' }}>
                    {state.websiteSwitch.currentWebsite}
                  </span>
                  {imageNotFound === false ? 
                    <Avatar
                      alt="Site Icon"
                      className={classes.siteAvatar}
                    >
                      <img
                        src={`https://hd-platform-icons.s3.us-west-1.amazonaws.com/Profile+Images/${state.websiteSwitch.currentWebsite}.png`}
                        onError={onError}
                        onLoad={onLoad}
                        alt="Site Icon"
                      />
                    </Avatar> : 
                    <Avatar>{avatarCopy}</Avatar>
                  }
                  {news ? (
                    <span className={classes.notificationsCountSmall}>
                      {news}
                    </span>
                  ) : (
                    ''
                  )}
                </>
              ) : (
                <Avatar>HD</Avatar>
              )}
            </Button>
          )}

          <Menu
            style={{ top: 52 }}
            anchorEl={menuTrigger}
            keepMounted
            open={Boolean(menuTrigger)}
            onClose={handleMenuClose}
            className={classes.menu}
          >
            {hasAccessGranted === true && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  openCampaignUpdates();
                }}
              >
                What&apos;s New {' '}
                {news ? (
                  <span className={classes.notificationsCount}>
                    {news}
                  </span>
                ) : (
                  ''
                )}
              </MenuItem>
            )}
            {state.websiteSwitch.currentWebsiteSettings.assignPageFeature &&
              state.content.assignPageFeature.total !== 0 && (
                <MenuItem aria-label="review pages button">
                  <Badge
                    badgeContent={state.content.assignPageFeature.total}
                    classes={{ badge: classes.themeBadge }}
                    onClick={() => {
                      history.push('/content/assigned-pages');
                      handleMenuClose();
                    }}
                  >
                    Assigned Pages
                  </Badge>
                </MenuItem>
              )}
            {state.websiteSwitch.allWebsites.length > 1 && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  switchWebsite();
                }}
                aria-label="switch website button"
              >
                Switch Website
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                handleMenuClose();
                openSettings();
              }}
            >
              Account Settings
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                handleLogout();
              }}
            >
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Sidebar>
        <UserLabel />
        {state.auth.user.role === 'content_only' ? (
          <>
            <List className={classes.list}>
              {hasAccessGranted === true && (
                <ListItem
                  button
                  onClick={() => {
                    history.push('/content');
                  }}
                >
                  <ListItemIcon>
                   {state.content.contentCountByType['Topics for Review'] ||
                    state.content.contentCountByType['Content for Review'] ||
                    state.content.contentCountByType['Revised Content for Review'] ? (
                      <Badge
                        badgeContent={
                          state.content.contentCountByType['Topics for Review'] +
                          state.content.contentCountByType['Content for Review'] + 
                          state.content.contentCountByType['Revised Content for Review']
                        }
                        classes={{ badge: classes.themeBadge }}
                      >
                        {currentPage.includes('content') ? (
                          <img
                            src={contentBlue}
                            className={`${classes.imgLogo} ${classes.blueColorCustomizer}`}
                            alt="content logo"
                          />
                        ) : (
                          <img
                            src={content}
                            className={`${classes.imgLogo} ${classes.greyColorCustomizer}`}
                            alt="content logo"
                          />
                        )}
                      </Badge>
                    ) : (
                      <>
                        {currentPage.includes('content') ? (
                          <img
                            src={contentBlue}
                            className={`${classes.imgLogo} ${classes.blueColorCustomizer}`}
                            alt="content logo"
                          />
                        ) : (
                          <img
                            src={content}
                            className={`${classes.imgLogo} ${classes.greyColorCustomizer}`}
                            alt="content logo"
                          />
                        )}
                      </>
                    )}
                  </ListItemIcon>
                  {currentPage.includes('content') ? (
                    <ListItemText primary="Content" className={classes.listItemFocused} />
                  ) : (
                    <ListItemText primary="Content" />
                  )}
                </ListItem>)
              }
              <ListItem
                button
                onClick={() => {
                  window.open(
                    'https://hennessey.zendesk.com/hc/en-us/requests',
                    '_blank'
                  );
                }}
              >
                <ListItemIcon>
                  {currentPage === 'support' ? (
                    <ChatBubbleOutlineOutlinedIcon className={classes.blueColorCustomizer} />
                  ) : (
                    <ChatBubbleOutlineOutlinedIcon className={classes.greyColorCustomizer} />
                  )}
                </ListItemIcon>
                {currentPage === 'support' ? (
                  <ListItemText primary="Support" className={classes.listItemFocused} />
                ) : (
                  <ListItemText primary="Support" />
                )}
              </ListItem>
            </List>
            <Divider className={classes.greyColorCustomizer} />
            <List className={classes.list}>
              <ListItem
                button
                onClick={() => {
                  history.push('/settings');
                }}
              >
                <ListItemIcon>
                  {currentPage === 'settings' ? (
                    <MoreHorizIcon className={classes.blueColorCustomizer} />
                  ) : (
                    <MoreHorizIcon className={classes.greyColorCustomizer} />
                  )}
                </ListItemIcon>
                {currentPage === 'settings' ? (
                  <ListItemText primary="Settings" className={classes.listItemFocused} />
                ) : (
                  <ListItemText primary="Settings" />
                )}
              </ListItem>
            </List>
          </>
        ) : (
          <>
            <List className={classes.list}>
              {hasAccessGranted === true && (
                <ListItem
                  button
                  onClick={() => {
                    history.push('/');
                  }}
                >
                  <ListItemIcon>
                    {currentPage === '' ? (
                      <DashboardOutlinedIcon className={classes.blueColorCustomizer} />
                    ) : (
                      <DashboardOutlinedIcon className={classes.greyColorCustomizer} />
                    )}
                  </ListItemIcon>
                  {currentPage === '' ? (
                    <ListItemText primary="Overview" className={classes.listItemFocused} />
                  ) : (
                    <ListItemText primary="Overview" />
                  )}
                </ListItem>
              )}
              {hasAccessGranted === true && (
                <ListItem
                  button
                  onClick={() => {
                    history.push('/campaign-updates');
                  }}
                >
                  <ListItemIcon>
                    <Badge classes={{ badge: classes.themeBadge }} badgeContent={news}>
                      <div style={{ marginLeft: 2 }}>
                        {currentPage.includes('campaign-updates') ? (
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 19 19"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M7.01 1.22V7.01001H1.22V1.22H7.01ZM8.24 0H0V8.24002H8.24V0V0Z"
                              fill="#00B5E2"
                            />
                            <path
                              d="M17.1699 1.22V7.01001H11.3799V1.22H17.1699V1.22ZM18.3999 0H10.1499V8.24002H18.3999V0Z"
                              fill="#00B5E2"
                            />
                            <path
                              d="M7.01 11.35V17.14H1.22V11.35H7.01ZM8.24 10.13H0V18.37H8.24V10.13V10.13Z"
                              fill="#00B5E2"
                            />
                            <path
                              d="M17.1699 11.35V17.14H11.3799V11.35H17.1699V11.35ZM18.3999 10.13H10.1499V18.37H18.3999V10.13Z"
                              fill="#00B5E2"
                            />
                            <path
                              d="M9.18004 15.18C12.7091 15.18 15.57 12.3191 15.57 8.79002C15.57 5.26092 12.7091 2.40002 9.18004 2.40002C5.65094 2.40002 2.79004 5.26092 2.79004 8.79002C2.79004 12.3191 5.65094 15.18 9.18004 15.18Z"
                              fill="#00B5E2"
                            />
                            <path d="M7.62988 11.2L12.2699 8.79L7.62988 6.38V11.2Z" fill="white" />
                          </svg>
                        ) : (
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 19 19"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M7.01 1.22V7.01001H1.22V1.22H7.01ZM8.24 0H0V8.24002H8.24V0V0Z"
                              fill="#BDBDBD"
                            />
                            <path
                              d="M17.1699 1.22V7.01001H11.3799V1.22H17.1699V1.22ZM18.3999 0H10.1499V8.24002H18.3999V0Z"
                              fill="#BDBDBD"
                            />
                            <path
                              d="M7.01 11.35V17.14H1.22V11.35H7.01ZM8.24 10.13H0V18.37H8.24V10.13V10.13Z"
                              fill="#BDBDBD"
                            />
                            <path
                              d="M17.1699 11.35V17.14H11.3799V11.35H17.1699V11.35ZM18.3999 10.13H10.1499V18.37H18.3999V10.13Z"
                              fill="#BDBDBD"
                            />
                            <path
                              d="M9.18004 15.18C12.7091 15.18 15.57 12.3191 15.57 8.79002C15.57 5.26092 12.7091 2.40002 9.18004 2.40002C5.65094 2.40002 2.79004 5.26092 2.79004 8.79002C2.79004 12.3191 5.65094 15.18 9.18004 15.18Z"
                              fill="#BDBDBD"
                            />
                            <path d="M7.62988 11.2L12.2699 8.79L7.62988 6.38V11.2Z" fill="white" />
                          </svg>
                        )}
                      </div>
                    </Badge>
                  </ListItemIcon>
                  {currentPage.includes('campaign-updates') ? (
                    <ListItemText primary="What's New " className={classes.listItemFocused} />
                  ) : (
                    <ListItemText primary="What's New " />
                  )}
                </ListItem>
              )}
              {hasAccessGranted === true && (
                <ListItem
                  button
                  onClick={() => {
                    history.push('/content');
                  }}
                >
                  <ListItemIcon>
                   {state.content.contentCountByType['Topics for Review'] ||
                    state.content.contentCountByType['Content for Review'] ||
                    state.content.contentCountByType['Revised Content for Review'] ? (
                      <Badge
                        badgeContent={
                          state.content.contentCountByType['Topics for Review'] +
                          state.content.contentCountByType['Content for Review'] + 
                          state.content.contentCountByType['Revised Content for Review']
                        }
                        classes={{ badge: classes.themeBadge }}
                      >
                        {currentPage.includes('content') ? (
                          <img
                            src={contentBlue}
                            className={`${classes.imgLogo} ${classes.blueColorCustomizer}`}
                            alt="content logo"
                          />
                        ) : (
                          <img
                            src={content}
                            className={`${classes.imgLogo} ${classes.greyColorCustomizer}`}
                            alt="content logo"
                          />
                        )}
                      </Badge>
                    ) : (
                      <>
                        {currentPage.includes('content') ? (
                          <img
                            src={contentBlue}
                            className={`${classes.imgLogo} ${classes.blueColorCustomizer}`}
                            alt="content logo"
                          />
                        ) : (
                          <img
                            src={content}
                            className={`${classes.imgLogo} ${classes.greyColorCustomizer}`}
                            alt="content logo"
                          />
                        )}
                      </>
                    )}
                  </ListItemIcon>
                  {currentPage.includes('content') ? (
                    <ListItemText primary="Content" className={classes.listItemFocused} />
                  ) : (
                    <ListItemText primary="Content" />
                  )}
                </ListItem>
              )}
              {hasAccessGranted === true && (
                <ListItem
                  style={{ flexWrap: 'wrap' }}
                  button
                  onClick={() => {
                    history.push('/analytics');
                  }}
                >
                  <ListItemIcon>
                    {currentPage === 'analytics' || currentPage === 'keywords/full-view' ? (
                      <img
                        src={analyticsBlue}
                        className={`${classes.imgLogo} ${classes.blueColorCustomizer}`}
                        alt="analytics logo"
                      />
                    ) : (
                      <img
                        src={analytics}
                        className={`${classes.imgLogo} ${classes.greyColorCustomizer}`}
                        alt="analytics logo"
                      />
                    )}
                  </ListItemIcon>
                  {currentPage === 'analytics' || currentPage === 'keywords/full-view' ? (
                    <ListItemText primary="Analytics" className={classes.listItemFocused} />
                  ) : (
                    <ListItemText primary="Analytics" />
                  )}

                  {/* <List className={classes.listChild}>
                    <ListItem
                      className={classes.listItemChild}
                      onClick={(event) => {
                        event.stopPropagation();
                        history.push('/analytics/gmb');
                      }}
                    >
                      <ListItemText
                        className={
                          currentPage.includes('analytics/gmb')
                            ? classes.listItemChildTextActive
                            : classes.listItemChildText
                        }
                        secondary="Google My Business"
                      />
                    </ListItem>

                    <ListItem
                      className={classes.listItemChild}
                      onClick={(event) => {
                        event.stopPropagation();
                        history.push('/analytics/semrush');
                      }}
                    >
                      <ListItemText className={classes.listItemChildText} secondary="SemRush" />
                    </ListItem>
                  </List> */}
                </ListItem>
              )}
              {hasAccessGranted === true && (
                <ListItem
                  style={{ flexWrap: 'wrap' }}
                  button
                  onClick={() => {
                    history.push('/backlinks');
                  }}
                >
                  <ListItemIcon>
                    {currentPage === 'backlinks' ? (
                      <LinkIcon className={classes.blueColorCustomizer} />
                    ) : (
                      <LinkIcon className={classes.greyColorCustomizer} />
                    )}
                  </ListItemIcon>
                  {currentPage === 'backlinks' ? (
                    <ListItemText primary="Backlinks" className={classes.listItemFocused} />
                  ) : (
                    <ListItemText primary="Backlinks" />
                  )}
                </ListItem>
              )}
              {hasAccessGranted === true && (
                <ListItem
                  button
                  onClick={() => {
                    history.push('/opportunities');
                  }}
                  aria-label="opportunities button"
                >
                  <ListItemIcon>
                    {currentPage === 'opportunities' ? (
                      <img
                        src={opportunitiesBlue}
                        className={`${classes.imgLogo} ${classes.blueColorCustomizer}`}
                        alt="opportunities logo"
                      />
                    ) : (
                      <img
                        src={opportunities}
                        className={`${classes.imgLogo} ${classes.greyColorCustomizer}`}
                        alt="opportunities logo"
                      />
                    )}
                  </ListItemIcon>
                  {currentPage === 'opportunities' ? (
                    <ListItemText primary="Opportunities" className={classes.listItemFocused} />
                  ) : (
                    <ListItemText primary="Opportunities" />
                  )}
                </ListItem>
              )}
              {(hasAccessGranted === true && leadsExist) && (
                <ListItem
                  button
                  onClick={() => {
                    history.push('/leads');
                  }}
                  aria-label="leads button"
                >
                  <ListItemIcon>
                    {currentPage === 'leads' ||
                    currentPage === 'leads/full-view' ||
                    currentPage === 'calls/full-view' ? (
                      <img
                        src={leadsBlue}
                        className={`${classes.imgLogo} ${classes.blueColorCustomizer}`}
                        alt="leads logo"
                      />
                    ) : (
                      <img
                        src={leads}
                        className={`${classes.imgLogo} ${classes.greyColorCustomizer}`}
                        alt="leads logo"
                      />
                    )}
                  </ListItemIcon>
                  {currentPage === 'leads' ||
                  currentPage === 'leads/full-view' ||
                  currentPage === 'calls/full-view' ? (
                    <ListItemText primary="Leads" className={classes.listItemFocused} />
                  ) : (
                    <ListItemText primary="Leads" />
                  )}
                </ListItem>
              )}
              {hasAccessGranted === true && (
                <ListItem
                  button
                  onClick={() => {
                    history.push('/paid-media');
                  }}
                >
                  <ListItemIcon>
                    {currentPage === 'paid-media' ? (
                      <TouchAppOutlinedIcon className={classes.blueColorCustomizer} />
                    ) : (
                      <TouchAppOutlinedIcon className={classes.greyColorCustomizer} />
                    )}
                  </ListItemIcon>
                  {currentPage === 'paid-media' ? (
                    <ListItemText primary="Paid Media" className={classes.listItemFocused} />
                  ) : (
                    <ListItemText primary="Paid Media" />
                  )}
                </ListItem>
              )}
              {hasAccessGranted === true && (
                <ListItem
                  button
                  onClick={() => {
                    history.push('/digital-pr');
                  }}
                  aria-label="Digital PR"
                >
                  <ListItemIcon>
                    {currentPage === 'digital-pr' ? (
                      <LinkIcon className={classes.blueColorCustomizer} />
                    ) : (
                      <LinkIcon className={classes.greyColorCustomizer} />
                    )}
                  </ListItemIcon>
                  {currentPage === 'digital-pr' ? (
                    <ListItemText primary="Digital PR" className={classes.listItemFocused} />
                  ) : (
                    <ListItemText primary="Digital PR" />
                  )}
                </ListItem>
              )}
              <ListItem
                button
                onClick={() => {
                  window.open(
                    'https://hennessey.zendesk.com/hc/en-us/requests',
                    '_blank'
                  );
                }}
              >
                <ListItemIcon>
                  {currentPage === 'support' ? (
                    <ChatBubbleOutlineOutlinedIcon className={classes.blueColorCustomizer} />
                  ) : (
                    <ChatBubbleOutlineOutlinedIcon className={classes.greyColorCustomizer} />
                  )}
                </ListItemIcon>
                {currentPage === 'support' ? (
                  <ListItemText primary="Support" className={classes.listItemFocused} />
                ) : (
                  <ListItemText primary="Support" />
                )}
              </ListItem>
            </List>
            <Divider className={classes.greyColorCustomizer} />
            <List className={classes.list}>
              <ListItem
                button
                onClick={() => {
                  history.push('/settings');
                }}
              >
                <ListItemIcon>
                  {currentPage === 'settings' ? (
                    <MoreHorizIcon className={classes.blueColorCustomizer} />
                  ) : (
                    <MoreHorizIcon className={classes.greyColorCustomizer} />
                  )}
                </ListItemIcon>
                {currentPage === 'settings' ? (
                  <ListItemText primary="Settings" className={classes.listItemFocused} />
                ) : (
                  <ListItemText primary="Settings" />
                )}
              </ListItem>
            </List>
          </>
        )}
      </Sidebar>
    </>
  );
};

export default StaticSidebar;
